<template>
  <v-row>
    <v-col cols="12" class="text-right pb-0 mt-0">
      <v-btn
        x-small elevation="10"
        class="text-xs font-weight-semibold"
        color="primary darken-2"
        :to="{ name: 'savings.clients_create_campaign' }"
      >
        <font-awesome-icon icon="fa-solid fa-plus" class="pe-1" />
        Create a Campaign
      </v-btn>
    </v-col>

    <!-- STEP 1 — Language dialog -->
    <v-dialog v-model="langDialog" max-width="320">
      <v-card>
        <v-card-title class="text-h6 justify-center pt-4">
          💬 Send message in...
        </v-card-title>
        <v-card-text class="text-center">
          Choose the language for the WhatsApp message:
        </v-card-text>
        <v-card-actions class="justify-center pb-5">
          <v-btn color="primary" @click="showPreview('en')">🇬🇧 English</v-btn>
          <v-btn color="success" class="ml-3" @click="showPreview('fr')">🇫🇷 Français</v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>

    <!-- STEP 2 — WhatsApp Preview dialog -->
    <v-dialog v-model="previewDialog" max-width="480">
      <v-card>

        <!-- Fake WhatsApp header -->
        <div style="background:#075E54; padding:12px 16px; display:flex; align-items:center; gap:12px; border-radius:4px 4px 0 0;">
          <div style="width:40px; height:40px; border-radius:50%; background:#25D366;
                       display:flex; align-items:center; justify-content:center;
                       font-weight:600; font-size:14px; color:#fff; flex-shrink:0;">
            {{ pendingItem ? initials(pendingItem.username) : '' }}
          </div>
          <div>
            <div style="color:#fff; font-size:15px; font-weight:500; line-height:1.2;">
              {{ pendingItem ? pendingItem.username : '' }}
            </div>
            <div style="color:#B2DFDB; font-size:12px;">
              {{ pendingItem ? pendingItem.phone_number : '' }}
            </div>
          </div>
          <v-spacer />
          <font-awesome-icon icon="fa-solid fa-phone" style="color:#B2DFDB; font-size:18px;" />
        </div>

        <!-- Campaign type badge -->
        <div v-if="pendingItem" style="background:#128C7E; padding:6px 16px; display:flex; align-items:center; gap:8px;">
          <font-awesome-icon :icon="campaignTypeIcon(pendingItem)" style="color:#fff; font-size:12px;" />
          <span style="color:#fff; font-size:12px; font-weight:500;">
            {{ campaignTypeLabel(pendingItem, selectedLang) }}
          </span>
        </div>

        <!-- Chat background -->
        <div style="background:#E5DDD5; padding:16px; min-height:260px;">
          <div style="display:flex; justify-content:flex-end;">
            <div
              style="background:#DCF8C6; border-radius:8px 0 8px 8px; padding:10px 13px;
                     max-width:90%; font-size:13px; color:#111; line-height:1.65;
                     box-shadow:0 1px 1px rgba(0,0,0,0.12);"
              v-html="previewBubble"
            ></div>
          </div>
        </div>

        <!-- Actions -->
        <v-card-actions class="px-4 pb-4 pt-2">
          <v-btn text @click="previewDialog = false; langDialog = true">
            <font-awesome-icon icon="fa-solid fa-arrow-left" class="me-1" /> Back
          </v-btn>
          <v-spacer />
          <v-btn color="success" @click="sendToWhatsApp()">
            <font-awesome-icon icon="fa-solid fa-paper-plane" class="me-2" />
            Open in WhatsApp
          </v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>

    <!-- ─── Main table card ─────────────────────────────────────────── -->
    <v-col cols="12">
      <v-card>
        <v-row class="mt-2 mx-2 pt-0">
          <v-col cols="12" class="px-4 pt-2 mt-1 mb-0 pb-0">
            <v-select
              v-model="selectedCampaignFilter"
              outlined dense
              :items="campaign_filter"
              label="Filter Campaigns By"
            ></v-select>
          </v-col>
          <v-col cols="5" xs="5" class="text-left mt-0 pt-0">
            <v-text-field v-model="selectedStartDate" dense type="date" label="Start Date"></v-text-field>
          </v-col>
          <v-col cols="5" xs="5" class="text-right mt-0 pt-0">
            <v-text-field v-model="selectedEndDate" dense type="date" label="End Date"></v-text-field>
          </v-col>
          <v-col cols="2" xs="2" class="text-right mt-0 pt-0">
            <spinner v-if="loading" class="text-right"></spinner>
            <v-btn v-else icon small class="primary" @click="getCampaigns()">
              <font-awesome-icon icon="fa-solid fa-search" />
            </v-btn>
          </v-col>
        </v-row>

        <v-card-title class="mt-0 pt-0">
          <span class="me-3">({{ requestedCampaigns.length }}) List Of Campaigns</span>
          <v-spacer></v-spacer>
          <v-text-field
            v-model="search" append-icon="mdi-magnify"
            label="Search" single-line hide-details
          ></v-text-field>
        </v-card-title>
        <h3 class="ml-3">Total Cash Saved: {{ savings_total }} F CFA</h3>

        <v-data-table
          :headers="headers" :items="requestedCampaigns"
          item-key="id" class="table-rounded"
          :items-per-page="10" disable-sort :search="search"
        >
          <!-- Name -->
          <template #[`item.name`]="{item}">
            <router-link :to="'/savings/clients/campaign/' + item.id">
              <div class="d-flex flex-column" style="cursor:pointer">
                <span class="d-block font-weight-semibold text--primary text-truncate">
                  {{ item.username }}: {{ item.name }}
                </span>
                <small v-if="item.target_date && item.target_amount">
                  Type: <b>Date & Amount</b> —
                  {{ formatPct(item.percentage_completed, 'en', 'MONEY_TIME_TARGET', item.remaining_days) }}
                </small>
                <small v-else-if="item.target_amount">
                  Type: <b>Amount</b> —
                  {{ formatPct(item.percentage_completed, 'en', 'MONEY_TARGET', item.remaining_days) }}
                </small>
                <small v-else-if="item.target_date">
                  Type: <b>Date</b> —
                  {{ formatPct(item.percentage_completed, 'en', 'TIME_TARGET', item.remaining_days) }}
                </small>
                <small v-else>Type: <b>Undefined</b> — ---</small>
              </div>
            </router-link>
          </template>

          <template #[`item.description`]="{item}">{{ item.description }}</template>

          <template #[`item.saved_amount`]="{item}">
            {{ fmt(item.saved_amount) }} F CFA
          </template>

          <template #[`item.target_amount`]="{item}">
            {{ fmt(item.target_amount) }} F CFA
            <span v-if="item.target_date"> — {{ item.target_date }}</span>
          </template>

          <template #[`item.status`]="{item}">
            <v-chip small :color="statusColor[item.status]" class="font-weight-medium">
              {{ item.status }}
            </v-chip>
          </template>

          <!-- WhatsApp Chat Button -->
          <template #[`item.phone_number`]="{item}">
            <v-btn class="success" elevation="6" icon small @click="promptLang(item)">
              <font-awesome-icon icon="fa-solid fa-comments" />
            </v-btn>
          </template>

          <template #[`item.get_date_str`]="{item}">{{ item.get_date_str }}</template>
        </v-data-table>
      </v-card>
    </v-col>
  </v-row>
</template>

<script>
import { mdiSquareEditOutline, mdiDotsVertical } from '@mdi/js'
import axios from 'axios'
import Spinner from "@/components/Spinner.vue"

export default {
  components: { Spinner },

  data() {
    const statusColor = {
      Ongoing    : 'primary',
      Completed  : 'success',
      Interrupted: 'error',
    }
    return {
      headers: [
        { text: 'Name',         value: 'name'         },
        { text: 'Description',  value: 'description'  },
        { text: 'Amount Saved', value: 'saved_amount'  },
        { text: 'Target',       value: 'target_amount' },
        { text: 'Status',       value: 'status'        },
        { text: 'Chat',         value: 'phone_number'  },
        { text: 'Date/Time',    value: 'get_date_str'  },
      ],
      selectedCampaignFilter: "All",
      campaign_filter: ["All", "Ongoing", "Completed", "Interrupted"],
      requestedCampaigns: [],
      statusColor,
      search: '',
      selectedStartDate: null,
      selectedEndDate: null,
      loading: false,
      savings_total: '',

      // Dialogs
      langDialog   : false,
      previewDialog: false,
      pendingItem  : null,
      selectedLang : 'en',
      previewBubble: '',

      icons: { mdiSquareEditOutline, mdiDotsVertical },
    }
  },

  mounted() {
    document.title = "Manager: Savings | List of Campaigns"
  },

  methods: {

    // ─── Campaign type ────────────────────────────────────────────────────────

    getCampaignType(item) {
      const hasAmount = !!item?.target_amount
      const hasDate   = !!item?.target_date
      if (hasAmount && hasDate) return 'MONEY_TIME_TARGET'
      if (hasAmount)            return 'MONEY_TARGET'
      if (hasDate)              return 'TIME_TARGET'
      return 'UNDEFINED_TARGET'
    },

    campaignTypeLabel(item, lang) {
      const type = this.getCampaignType(item)
      const labels = {
        MONEY_TIME_TARGET: { en: '🎯 Amount + Date target', fr: '🎯 Objectif montant + date' },
        MONEY_TARGET:      { en: '💰 Amount target',        fr: '💰 Objectif montant'        },
        TIME_TARGET:       { en: '📅 Date target',          fr: '📅 Objectif date'           },
        UNDEFINED_TARGET:  { en: '❓ No target defined',    fr: '❓ Aucun objectif défini'   },
      }
      return labels[type]?.[lang] ?? type
    },

    campaignTypeIcon(item) {
      const icons = {
        MONEY_TIME_TARGET: 'fa-solid fa-bullseye',
        MONEY_TARGET:      'fa-solid fa-piggy-bank',
        TIME_TARGET:       'fa-solid fa-calendar',
        UNDEFINED_TARGET:  'fa-solid fa-question',
      }
      return icons[this.getCampaignType(item)] ?? 'fa-solid fa-question'
    },

    // ─── Formatters ───────────────────────────────────────────────────────────

    initials(name = '') {
      return name.split(' ').map(w => w[0]).join('').toUpperCase().slice(0, 2)
    },

    fmt(amount) {
      return Math.round(Number(amount || 0)).toLocaleString('fr-FR')
    },

    /**
     * Formate le progrès selon le type de campagne :
     * - TIME_TARGET        → "X jours restants" / "X days left"
     * - MONEY_TIME_TARGET  → "50% — 60 jours restants"
     * - MONEY_TARGET       → "50%"
     * - UNDEFINED_TARGET   → "—"
     */
    formatPct(raw, lang, type, remainingDays) {
      const fr = lang === 'fr'

      // ── TIME_TARGET : progrès = jours restants ────────────────────────
      if (type === 'TIME_TARGET') {
        const days = parseInt(remainingDays, 10)
        if (isNaN(days)) return '—'
        if (fr) {
          return days > 0
            ? `${days} jour${days > 1 ? 's' : ''} restant${days > 1 ? 's' : ''}`
            : 'Échéance atteinte'
        }
        return days > 0
          ? `${days} day${days > 1 ? 's' : ''} left`
          : 'Deadline reached'
      }

      // ── Autres types : pourcentage ────────────────────────────────────
      if (raw === null || raw === undefined || raw === '') return '—'
      const str      = String(raw)
      const numMatch = str.match(/([\d.]+)/)
      if (!numMatch) return str

      const num     = parseFloat(numMatch[1])
      const rounded = Number.isInteger(num) ? num : parseFloat(num.toFixed(1))
      const pctStr  = `${rounded}%`

      // MONEY_TIME_TARGET : extrait aussi les jours restants si présents
      if (type === 'MONEY_TIME_TARGET') {
        const days = parseInt(remainingDays, 10)
        if (!isNaN(days) && days > 0) {
          return fr
            ? `${pctStr} — ${days} jour${days > 1 ? 's' : ''} restant${days > 1 ? 's' : ''}`
            : `${pctStr} — ${days} day${days > 1 ? 's' : ''} left`
        }
        if (!isNaN(days) && days <= 0) {
          return fr ? `${pctStr} — Échéance atteinte` : `${pctStr} — Deadline reached`
        }
      }

      return pctStr
    },

    // ─── Dialog flow ──────────────────────────────────────────────────────────

    promptLang(item) {
      this.pendingItem = item
      this.langDialog  = true
    },

    showPreview(lang) {
      this.langDialog    = false
      this.selectedLang  = lang
      this.previewBubble = this.buildBubbleHtml(this.pendingItem, lang)
      this.previewDialog = true
    },

    sendToWhatsApp() {
      this.previewDialog = false
      window.open(this.buildWhatsAppUrl(this.pendingItem, this.selectedLang), '_blank')
      this.pendingItem = null
    },

    // ─── Message builders ─────────────────────────────────────────────────────

    buildBubbleHtml(item, lang) {
      if (!item) return ''
      const type = this.getCampaignType(item)
      const fr   = lang === 'fr'

      const user   = item.username    || '—'
      const name   = item.name        || '—'
      const desc   = item.description || '—'
      const saved  = this.fmt(item.saved_amount)
      const target = this.fmt(item.target_amount)
      const date   = item.target_date || '—'
      // ✅ Les 4 arguments passés correctement
      const pct    = this.formatPct(item.percentage_completed, lang, type, item.remaining_days)

      const greeting  = fr
        ? `<p style="margin:0 0 6px">Bonjour <b>${user}</b> 💫,</p>`
        : `<p style="margin:0 0 6px">Hello <b>${user}</b> 💫,</p>`

      const note      = fr
        ? `<p style="margin:0 0 8px">📝 <b>Note :</b> ${desc}</p>`
        : `<p style="margin:0 0 8px">📝 <b>Campaign Note:</b> ${desc}</p>`

      const savedLine = fr
        ? `<p style="margin:0 0 4px">💰 <b>Épargne actuelle :</b> ${saved} F CFA</p>`
        : `<p style="margin:0 0 4px">💰 <b>Current Savings:</b> ${saved} F CFA</p>`

      const timestamp = `<p style="text-align:right; margin:8px 0 0; font-size:11px; color:#888;">${fr ? 'Aperçu' : 'Preview'} ✓✓</p>`

      let intro = '', targets = '', closing = ''

      if (type === 'MONEY_TIME_TARGET') {
        intro = fr
          ? `<p style="margin:0 0 8px">Voici une mise à jour de votre campagne <b>${name}</b>, qui vise un montant <i>et</i> une date cible.</p>`
          : `<p style="margin:0 0 8px">Here's an update on your savings campaign <b>${name}</b>, which targets both an amount <i>and</i> a date.</p>`
        targets = fr
          ? `${savedLine}
             <p style="margin:0 0 4px">🎯 <b>Montant cible :</b> ${target} F CFA</p>
             <p style="margin:0 0 4px">📅 <b>Date limite :</b> ${date}</p>
             <p style="margin:0 0 8px">📈 <b>Progression :</b> ${pct}</p>`
          : `${savedLine}
             <p style="margin:0 0 4px">🎯 <b>Target Amount:</b> ${target} F CFA</p>
             <p style="margin:0 0 4px">📅 <b>Target Date:</b> ${date}</p>
             <p style="margin:0 0 8px">📈 <b>Progress:</b> ${pct}</p>`
        closing = fr
          ? `<p style="margin:0 0 6px">Chaque dépôt vous rapproche de votre objectif. Restez régulier !</p>
             <p style="margin:0">Merci de faire confiance à <b>PettyCash</b>. Nous sommes ravis de vous accompagner ! 🚀</p>`
          : `<p style="margin:0 0 6px">Every deposit brings you closer to your goal. Stay consistent!</p>
             <p style="margin:0">Thank you for choosing <b>PettyCash</b>. We're excited to support your journey! 🚀</p>`

      } else if (type === 'MONEY_TARGET') {
        intro = fr
          ? `<p style="margin:0 0 8px">Voici une mise à jour de votre campagne <b>${name}</b>, qui vise un montant cible.</p>`
          : `<p style="margin:0 0 8px">Here's an update on your savings campaign <b>${name}</b>, which targets a specific amount.</p>`
        targets = fr
          ? `${savedLine}
             <p style="margin:0 0 4px">🎯 <b>Montant cible :</b> ${target} F CFA</p>
             <p style="margin:0 0 8px">📈 <b>Progression :</b> ${pct}</p>`
          : `${savedLine}
             <p style="margin:0 0 4px">🎯 <b>Target Amount:</b> ${target} F CFA</p>
             <p style="margin:0 0 8px">📈 <b>Progress:</b> ${pct}</p>`
        closing = fr
          ? `<p style="margin:0 0 6px">Continuez à épargner régulièrement — chaque franc compte !</p>
             <p style="margin:0">Merci de faire confiance à <b>PettyCash</b> 🚀</p>`
          : `<p style="margin:0 0 6px">Keep saving consistently — every franc counts!</p>
             <p style="margin:0">Thank you for choosing <b>PettyCash</b> 🚀</p>`

      } else if (type === 'TIME_TARGET') {
        intro = fr
          ? `<p style="margin:0 0 8px">Voici une mise à jour de votre campagne <b>${name}</b>, dont l'objectif est une date cible.</p>`
          : `<p style="margin:0 0 8px">Here's an update on your savings campaign <b>${name}</b>, which runs until a target date.</p>`
        targets = fr
          ? `${savedLine}
             <p style="margin:0 0 4px">📅 <b>Date cible :</b> ${date}</p>
             <p style="margin:0 0 8px">⏳ <b>Temps restant :</b> ${pct}</p>`
          : `${savedLine}
             <p style="margin:0 0 4px">📅 <b>Target Date:</b> ${date}</p>
             <p style="margin:0 0 8px">⏳ <b>Time remaining:</b> ${pct}</p>`
        closing = fr
          ? `<p style="margin:0 0 6px">Chaque jour compte. Continuez à construire votre avenir financier !</p>
             <p style="margin:0">Merci de faire confiance à <b>PettyCash</b> 🚀</p>`
          : `<p style="margin:0 0 6px">Every day counts. Keep building your financial future!</p>
             <p style="margin:0">Thank you for choosing <b>PettyCash</b> 🚀</p>`

      } else {
        intro = fr
          ? `<p style="margin:0 0 8px">Voici une mise à jour de votre campagne <b>${name}</b>.</p>`
          : `<p style="margin:0 0 8px">Here's an update on your savings campaign <b>${name}</b>.</p>`
        targets = savedLine
        closing = fr
          ? `<p style="margin:0 0 6px">Continuez à épargner — chaque contribution compte !</p>
             <p style="margin:0">Merci de faire confiance à <b>PettyCash</b> 🚀</p>`
          : `<p style="margin:0 0 6px">Keep saving — every contribution matters!</p>
             <p style="margin:0">Thank you for choosing <b>PettyCash</b> 🚀</p>`
      }

      return `${greeting}${intro}${note}${targets}${closing}${timestamp}`
    },

    buildWhatsAppUrl(item, lang) {
      if (!item) return '#'
      const type = this.getCampaignType(item)
      const fr   = lang === 'fr'

      const phone  = String(item.phone_number || '').length === 9
        ? '237' + item.phone_number
        : item.phone_number

      const user   = item.username    || '—'
      const name   = item.name        || '—'
      const desc   = item.description || '—'
      const saved  = this.fmt(item.saved_amount)
      const target = this.fmt(item.target_amount)
      const date   = item.target_date || '—'
      // ✅ Les 4 arguments passés correctement
      const pct    = this.formatPct(item.percentage_completed, lang, type, item.remaining_days)

      let msg = ''

      if (type === 'MONEY_TIME_TARGET') {
        msg = fr
          ? `Bonjour *${user}* 💫,%0a%0a` +
            `Voici une mise à jour de votre campagne *${name}* (objectif montant + date).%0a%0a` +
            `📝 *Note :* ${desc}%0a%0a` +
            `💰 *Épargne actuelle :* ${saved} F CFA%0a` +
            `🎯 *Montant cible :* ${target} F CFA%0a` +
            `📅 *Date limite :* ${date}%0a` +
            `📈 *Progression :* ${pct}%0a%0a` +
            `Chaque dépôt vous rapproche de votre objectif. Restez régulier !%0a%0a` +
            `Merci de faire confiance à *PettyCash* 🚀`
          : `Hello *${user}* 💫,%0a%0a` +
            `Here's an update on your savings campaign *${name}* (Amount + Date target).%0a%0a` +
            `📝 *Campaign Note:* ${desc}%0a%0a` +
            `💰 *Current Savings:* ${saved} F CFA%0a` +
            `🎯 *Target Amount:* ${target} F CFA%0a` +
            `📅 *Target Date:* ${date}%0a` +
            `📈 *Progress:* ${pct}%0a%0a` +
            `Every deposit brings you closer to your goal. Stay consistent!%0a%0a` +
            `Thank you for choosing *PettyCash* 🚀`

      } else if (type === 'MONEY_TARGET') {
        msg = fr
          ? `Bonjour *${user}* 💫,%0a%0a` +
            `Voici une mise à jour de votre campagne *${name}* (objectif montant).%0a%0a` +
            `📝 *Note :* ${desc}%0a%0a` +
            `💰 *Épargne actuelle :* ${saved} F CFA%0a` +
            `🎯 *Montant cible :* ${target} F CFA%0a` +
            `📈 *Progression :* ${pct}%0a%0a` +
            `Continuez à épargner — chaque franc compte !%0a%0a` +
            `Merci de faire confiance à *PettyCash* 🚀`
          : `Hello *${user}* 💫,%0a%0a` +
            `Here's an update on your savings campaign *${name}* (Amount target).%0a%0a` +
            `📝 *Campaign Note:* ${desc}%0a%0a` +
            `💰 *Current Savings:* ${saved} F CFA%0a` +
            `🎯 *Target Amount:* ${target} F CFA%0a` +
            `📈 *Progress:* ${pct}%0a%0a` +
            `Keep saving consistently — every franc counts!%0a%0a` +
            `Thank you for choosing *PettyCash* 🚀`

      } else if (type === 'TIME_TARGET') {
        msg = fr
          ? `Bonjour *${user}* 💫,%0a%0a` +
            `Voici une mise à jour de votre campagne *${name}* (objectif date).%0a%0a` +
            `📝 *Note :* ${desc}%0a%0a` +
            `💰 *Épargne actuelle :* ${saved} F CFA%0a` +
            `📅 *Date cible :* ${date}%0a` +
            `⏳ *Temps restant :* ${pct}%0a%0a` +
            `Chaque jour compte. Continuez à construire votre avenir financier !%0a%0a` +
            `Merci de faire confiance à *PettyCash* 🚀`
          : `Hello *${user}* 💫,%0a%0a` +
            `Here's an update on your savings campaign *${name}* (Date target).%0a%0a` +
            `📝 *Campaign Note:* ${desc}%0a%0a` +
            `💰 *Current Savings:* ${saved} F CFA%0a` +
            `📅 *Target Date:* ${date}%0a` +
            `⏳ *Time remaining:* ${pct}%0a%0a` +
            `Every day counts. Keep building your financial future!%0a%0a` +
            `Thank you for choosing *PettyCash* 🚀`

      } else {
        msg = fr
          ? `Bonjour *${user}* 💫,%0a%0a` +
            `Voici une mise à jour de votre campagne *${name}*.%0a%0a` +
            `📝 *Note :* ${desc}%0a%0a` +
            `💰 *Épargne actuelle :* ${saved} F CFA%0a%0a` +
            `Continuez à épargner — chaque contribution compte !%0a%0a` +
            `Merci de faire confiance à *PettyCash* 🚀`
          : `Hello *${user}* 💫,%0a%0a` +
            `Here's an update on your savings campaign *${name}*.%0a%0a` +
            `📝 *Campaign Note:* ${desc}%0a%0a` +
            `💰 *Current Savings:* ${saved} F CFA%0a%0a` +
            `Keep saving — every contribution matters!%0a%0a` +
            `Thank you for choosing *PettyCash* 🚀`
      }

      return `https://api.whatsapp.com/send?phone=${phone}&text=${msg}`
    },

    // ─── API ──────────────────────────────────────────────────────────────────

    async getCampaigns() {
      if (!this.selectedCampaignFilter) this.selectedCampaignFilter = 'All'
      if (!this.selectedStartDate)      this.selectedStartDate = 'null'
      if (!this.selectedEndDate)        this.selectedEndDate = 'null'

      this.loading = true
      await axios
        .get(`/api/v1/manager/get/campaigns/${this.selectedCampaignFilter}/${this.selectedStartDate}/${this.selectedEndDate}/`)
        .then(response => {
          this.requestedCampaigns = response.data["campaigns"]
          this.savings_total      = response.data["total_saved"]
        })
        .catch(error => {
          const msg = error.response
            ? `${error.response.status} ${error.response.statusText}`
            : "An error occured, please check your internet connection and try again later."
          this.$store.commit('setSnackBarMessage', msg)
          this.$store.commit('setSnackBarColor', "red darken-4")
          this.$store.commit('activateSnackBar', true)
        })
      this.loading = false
    },
  },
}
</script>
